import React from 'react';
import type { MatchSummary, PendingDocument } from '@schemas/api';
import { AlertTriangle, CheckCircle2, CircleDashed, Link2 } from 'lucide-react';

type MatchStatusBadgeProps = {
  document: Pick<PendingDocument, 'match_status' | 'match_summary'> & {
    match_status?: string | null;
    match_summary?: MatchSummary | null;
  };
  showDetail?: boolean;
};

const statusLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const fallbackSummary = (status?: string | null): MatchSummary | null => {
  if (!status) {
    return null;
  }

  if (status === 'unmatched') {
    return {
      status,
      phase: 'not_started',
      label: 'No PO found',
      detail: 'No PO match yet',
      waiting_for: [],
    };
  }

  if (['matching', 'candidate_found', 'preliminary_match'].includes(status)) {
    return {
      status,
      phase: 'preliminary',
      label: 'Preliminary',
      detail: 'PO linked; waiting for counterpart',
      waiting_for: [],
    };
  }

  if (status === 'waiting_for_counterpart') {
    return {
      status,
      phase: 'waiting',
      label: 'Preliminary',
      detail: 'Waiting for counterpart document',
      waiting_for: [],
    };
  }

  return {
    status,
    phase: status === 'matched' ? 'complete' : 'exception',
    label: statusLabel(status),
    detail: status === 'matched' ? '3-way match complete' : 'Needs match review',
    waiting_for: [],
  };
};

const toneClass = (summary: MatchSummary) => {
  if (summary.status === 'unmatched') {
    return 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300';
  }
  if (summary.status === 'matched') {
    return 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300';
  }
  if (['matching', 'candidate_found', 'preliminary_match', 'waiting_for_counterpart'].includes(summary.status)) {
    return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300';
  }
  if (['hold', 'split'].includes(summary.status)) {
    return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';
  }
  return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300';
};

const MatchIcon = ({ summary }: { summary: MatchSummary }) => {
  if (summary.status === 'matched') {
    return <CheckCircle2 className="h-3.5 w-3.5" />;
  }
  if (['matching', 'candidate_found', 'preliminary_match', 'waiting_for_counterpart'].includes(summary.status)) {
    return <Link2 className="h-3.5 w-3.5" />;
  }
  if (['hold', 'split', 'reject', 'rejected'].includes(summary.status)) {
    return <AlertTriangle className="h-3.5 w-3.5" />;
  }
  return <CircleDashed className="h-3.5 w-3.5" />;
};

export const MatchStatusBadge: React.FC<MatchStatusBadgeProps> = ({
  document,
  showDetail = false,
}) => {
  const summary = document.match_summary ?? fallbackSummary(document.match_status);

  if (!summary) {
    return null;
  }

  return (
    <span
      title={summary.detail || summary.label}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold leading-none ${toneClass(summary)}`}
    >
      <MatchIcon summary={summary} />
      <span className="truncate">{summary.label}</span>
      {showDetail && summary.detail ? (
        <span className="hidden truncate font-medium opacity-80 sm:inline">
          {summary.detail}
        </span>
      ) : null}
    </span>
  );
};

export default MatchStatusBadge;
